<template>
  <div>
    <!-- Dialog de Detalhes (lista de medicamentos) — oculto no modo openDirectAdd -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="1000px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-pill</v-icon>
            <span class="ds-dialog-title">{{ dialogTitle }}</span>
          </div>
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="white"
                  @click="openAddDialog"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Adicionar medicamento</span>
            </v-tooltip>
            <v-btn
              icon
              color="white"
              class="close-btn"
              @click="closeDetailsDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section v-if="medications.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Medicamento
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 160px">
                    Dosagem
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 150px">
                    Data
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 160px">
                    Status
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 90px">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in medications" :key="item.id" class="history-row">
                  <td class="pa-4">
                    <span class="body-2 font-weight-medium">{{ item.name }}</span>
                  </td>
                  <td class="pa-4">
                    <span class="body-2">{{ item.dosage || "—" }}</span>
                  </td>
                  <td class="text-no-wrap pa-4">
                    <div class="d-flex align-center">
                      <v-icon small color="grey" class="mr-2">mdi-calendar</v-icon>
                      <span class="body-2">{{ formatDate(item.created_at) }}</span>
                    </div>
                  </td>
                  <td class="pa-4">
                    <v-select
                      :value="item.status || 'using'"
                      :items="statusOptions"
                      :loading="statusUpdatingId === item.id"
                      :disabled="statusUpdatingId === item.id"
                      dense
                      hide-details
                      outlined
                      :color="statusColor(item.status)"
                      item-color="catGreen"
                      class="status-select"
                      @change="changeStatus(item, $event)"
                    >
                      <template v-slot:selection="{ item: opt }">
                        <v-chip x-small :color="statusColor(opt.value)" text-color="white">
                          {{ opt.text }}
                        </v-chip>
                      </template>
                    </v-select>
                  </td>
                  <td class="pa-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="catGreen"
                          class="action-btn mr-1"
                          @click="openLinker(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-link-variant</v-icon>
                        </v-btn>
                      </template>
                      <span>Vincular</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="error"
                          class="action-btn"
                          @click="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <!-- Estado vazio -->
          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">mdi-pill-off</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">Nenhum medicamento registrado.</p>
            <v-btn color="catGreen" outlined small class="mt-4" @click="openAddDialog">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeiro medicamento
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Adicionar -->
    <v-dialog v-model="formDialogVisible" max-width="600px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center" style="position: relative; z-index: 1">
            <v-icon color="white" class="mr-2">mdi-plus-circle-outline</v-icon>
            <span class="ds-dialog-title">Adicionar medicamento</span>
          </div>
          <v-btn icon color="white" class="close-btn" @click="closeFormDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-pill</v-icon>
                <span>Medicamentos</span>
              </div>
            </div>
            <MedicationSearchField
              :key="'med-' + formRenderKey"
              v-model="selectedMedications"
            />
          </section>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="closeFormDialog">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="catGreen"
            dark
            :disabled="!canSave"
            :loading="formLoading"
            depressed
            class="px-6"
            @click="saveData"
          >
            <v-icon small left>mdi-plus</v-icon>
            Adicionar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vincular medicamento ↔ doença (correlação) -->
    <CorrelationLinker
      v-model="linkerOpen"
      source-type="medication"
      :source-id="linkerMed ? linkerMed.id : null"
      :source-label="linkerMed ? linkerMed.name : ''"
      :patient-id="patientId"
      @changed="$emit('data-saved')"
    />
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  createMedications,
  deleteMedication,
  updateMedication,
} from "@/services/patientMedicationService";
import { getConditionsByPatient } from "@/services/conditionService";
import MedicationSearchField from "@/features/patients/profile-dashboard/fields/MedicationSearchField.vue";
import CorrelationLinker from "@/features/patients/profile-dashboard/shared/CorrelationLinker.vue";
import { formatDateTime } from "@/utils/datetime";

export default {
  name: "EditMedicationsDialog",

  components: {
    MedicationSearchField,
    CorrelationLinker,
  },

  props: {
    value: { type: Boolean, default: false },
    dialogTitle: { type: String, default: "Medicamentos do paciente" },
    medications: { type: Array, default: () => [] },
    patientId: { type: [String, Number], required: true },
    // Quando true, abre direto o form de adicionar (pula o dialog de lista).
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },

  data() {
    return {
      formDialogVisible: false,
      selectedMedications: [],
      formLoading: false,
      deleteLoading: false,
      itemToDelete: null,
      formRenderKey: 0,
      statusUpdatingId: null,
      statusOptions: [
        { text: "Usando", value: "using" },
        { text: "Parou", value: "stopped" },
        { text: "Não sei", value: "unknown" },
      ],
      // Correlação medicamento ↔ doença
      linkerOpen: false,
      linkerMed: null,
      conditions: [],
      loadingConditions: false,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    canSave() {
      return !this.formLoading && this.selectedMedications.length > 0;
    },
  },

  watch: {
    value(val) {
      if (val && this.openDirectAdd) {
        this.$nextTick(() => this.openAddDialog());
      }
    },
    formDialogVisible(val) {
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    formatDate: formatDateTime,
    closeDetailsDialog() {
      this.$emit("input", false);
    },

    openAddDialog() {
      this.selectedMedications = [];
      this.formRenderKey += 1;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedMedications = [];
    },

    async saveData() {
      if (this.selectedMedications.length === 0) {
        this.warning("Adicione ao menos um medicamento");
        return;
      }
      this.formLoading = true;
      try {
        await createMedications(this.patientId, this.selectedMedications);
        // salvia-781: guarda antes de fechar o form, que limpa a selecao.
        const registrado = this.selectedMedications;
        this.success("Medicamento(s) adicionado(s) com sucesso!");
        this.closeFormDialog();
        this.$emit("data-saved", registrado);
      } catch (err) {
        console.error("Erro ao salvar medicamentos:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao salvar os medicamentos");
      } finally {
        this.formLoading = false;
      }
    },

    async openLinker(med) {
      this.linkerMed = med;
      if (this.conditions.length === 0) {
        this.loadingConditions = true;
        try {
          this.conditions = await getConditionsByPatient(this.patientId);
        } catch (err) {
          console.error("Erro ao carregar condições:", err);
          this.conditions = [];
        } finally {
          this.loadingConditions = false;
        }
      }
      this.linkerOpen = true;
    },

    statusColor(status) {
      return (
        {
          using: "#15803D",
          stopped: "#B91C1C",
          unknown: "#78716C",
        }[status] || "#15803D"
      );
    },

    // Muda o status de uso (mantém o medicamento no histórico, não apaga).
    async changeStatus(item, status) {
      if (!status || status === item.status) return;
      this.statusUpdatingId = item.id;
      try {
        await updateMedication(item.id, { status });
        this.success("Status atualizado!");
        this.$emit("data-saved");
      } catch (err) {
        console.error("Erro ao atualizar status:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao atualizar o status");
      } finally {
        this.statusUpdatingId = null;
      }
    },

    async confirmDelete(item) {
      this.itemToDelete = item;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: `Tem certeza que deseja remover "${item.name}"?`,
        hint: "Prefira mudar o status (ex.: \"Parou\") para manter no histórico. A exclusão não poderá ser desfeita.",
      });
      if (!ok) {
        this.itemToDelete = null;
        return;
      }
      await this.deleteData();
    },

    async deleteData() {
      if (!this.itemToDelete) return;
      this.deleteLoading = true;
      try {
        await deleteMedication(this.itemToDelete.id);
        this.success("Medicamento removido com sucesso!");
        this.itemToDelete = null;
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir medicamento:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover o medicamento");
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog (tema Verde — Medicamentos) ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: var(--ds-white);
}

.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}

.ds-dialog-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.history-table thead th {
  background-color: var(--ds-primary-surface) !important;
  border-bottom: 2px solid var(--ds-primary-border) !important;
  letter-spacing: 0.4px;
  color: var(--ds-primary-deep) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.history-row {
  transition: background-color 0.15s ease;
}

.history-row:hover {
  background-color: var(--ds-primary-surface) !important;
}

.history-row td {
  border-bottom: 1px solid var(--ds-primary-border) !important;
  color: var(--ds-text-600);
}

.action-btn {
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.action-btn:hover {
  transform: scale(1.15);
}

.status-select {
  max-width: 150px;
}
.status-select :deep(.v-input__slot) {
  min-height: 34px !important;
}
</style>
